import { useState } from 'react';
import axios from 'axios';
import { Mail, MapPin, Send, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-black" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-teal-400 uppercase tracking-[0.3em] text-xs font-medium mb-4">
            Get in Touch
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Contact
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Interested in a piece? Have a public space that needs art? 
            I'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-8">
                Let's Connect
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/50 mb-1">
                      Email
                    </p>
                    <a 
                      href="mailto:richardsonjim@gmail.com" 
                      className="text-white hover:text-teal-400 transition-colors font-medium"
                      data-testid="email-link"
                    >
                      richardsonjim@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/50 mb-1">
                      Location
                    </p>
                    <p className="text-white font-medium">
                      Victoria, BC, Canada
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-8 bg-neutral-900 border border-white/10">
                <h3 className="font-heading text-xl font-semibold text-white mb-4">
                  Looking for Public Art?
                </h3>
                <p className="text-white/60 leading-relaxed">
                  If you know of a public space that would be improved by some art, 
                  I'd love to hear about it. My stone on glass pieces are designed 
                  specifically for public spaces—modern, simple, clean, yet warm.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-neutral-900 p-8 lg:p-12 shadow-soft border border-white/10">
                {submitted ? (
                  <div className="text-center py-12" data-testid="success-message">
                    <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="h-8 w-8 text-teal-400" />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                      Message Sent!
                    </h3>
                    <p className="text-white/60 mb-6">
                      Thank you for reaching out. I'll get back to you soon.
                    </p>
                    <Button 
                      onClick={() => setSubmitted(false)}
                      variant="outline" 
                      className="btn-secondary"
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h2 className="font-heading text-2xl font-semibold text-white mb-8">
                      Send a Message
                    </h2>

                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="name" className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                          Your Name
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="form-input"
                          placeholder="John Doe"
                          data-testid="name-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                          Email Address
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="form-input"
                          placeholder="john@example.com"
                          data-testid="email-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                          Message
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          className="form-input min-h-[150px]"
                          placeholder="I'm interested in your artwork..."
                          data-testid="message-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full"
                        data-testid="submit-btn"
                      >
                        {loading ? (
                          <>
                            <div className="spinner mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message
                            <Send className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
